import React, { useEffect, useState, useRef } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import CustomizedSuccessCard from '../Components/CustomizedSuccessCard';
import NIALogo from '../Assets/Logo.png';

const DashboardIA = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [sisterIAs, setSisterIAs] = useState([]);
  const [members, setMembers] = useState([]);
  const [sisterPage, setSisterPage] = useState(1);
  const [membersPage, setMembersPage] = useState(1);
  const [machineryPage, setMachineryPage] = useState(1);
  const [pageSize] = useState(10);
  const [sisterSearchTerm, setSisterSearchTerm] = useState('');
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [editProfileData, setEditProfileData] = useState(null);
  const [profileSaving, setProfileSaving] = useState(false);
  const [memberSearchTerm, setMemberSearchTerm] = useState('');
  const [machinerySearchTerm, setMachinerySearchTerm] = useState('');
  const [successMessage, setSuccessMessage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [machineries, setMachineries] = useState([]);
  const [profile, setProfile] = useState(null);
  const [profileLoaded, setProfileLoaded] = useState(false);
  const [overviewLoading, setOverviewLoading] = useState(false);
  const [overviewLoaded, setOverviewLoaded] = useState(false);
  const [overviewAnalytics, setOverviewAnalytics] = useState({
    totalMembers: 0,
    totalMachineries: 0,
    maleMembers: 0,
    femaleMembers: 0,
    unspecifiedMembers: 0,
    averageFarmArea: 0,
    rsbsaRegisteredCount: 0,
    machineryFunctionalUnits: 0,
    machineryNotFunctionalUnits: 0,
    acquisitionBreakdown: [],
  });
  const [editingMemberId, setEditingMemberId] = useState(null);
  const [isAddMemberOpen, setIsAddMemberOpen] = useState(false);
  const [editingMachineryId, setEditingMachineryId] = useState(null);
  const [isAddMachineryOpen, setIsAddMachineryOpen] = useState(false);
  const [editFormData, setEditFormData] = useState({});
  const [editMachineryData, setEditMachineryData] = useState({});
  const [newMemberData, setNewMemberData] = useState({});
  const [newMachineryData, setNewMachineryData] = useState({});
  const [isImportingMembers, setIsImportingMembers] = useState(false);
  const [isImportingMachineries, setIsImportingMachineries] = useState(false);
  const [isExportingMembers, setIsExportingMembers] = useState(false);
  const [isExportingMachineries, setIsExportingMachineries] = useState(false);
  const memberFileInputRef = useRef(null);
  const machineryFileInputRef = useRef(null);

  const normalizeYearsList = (value) => {
    const years = Array.isArray(value)
      ? value.map((item) => String(item?.years ?? item?.year ?? item?.value ?? item ?? '').trim())
      : typeof value === 'string'
        ? value.split(',').map((item) => item.trim())
        : [];

    return [...new Set(years.filter(Boolean))];
  };

  const iaUser = (() => {
    try {
      const storedUser = JSON.parse(localStorage.getItem('iaUser') || '{}');
      const availableYears = normalizeYearsList(
        storedUser.availableYears ?? storedUser.yearsList ?? storedUser.available_years ?? storedUser.years
      );
      const normalizedYear = String(
        storedUser.selectedYear ?? storedUser.year ?? storedUser.years ?? availableYears[0] ?? ''
      ).trim();
      const result = {
        ...storedUser,
        year: normalizedYear,
        years: normalizedYear, // Keep both to be safe
        selectedYear: normalizedYear,
        availableYears: availableYears.length > 0 ? availableYears : (normalizedYear ? [normalizedYear] : []),
      };
      console.log('[DashboardIA] Loaded iaUser from localStorage:', {
        availableYears,
        normalizedYear,
        iaUser: result,
      });
      return result;
    } catch (err) {
      console.error('[DashboardIA] Failed to load iaUser from localStorage:', err);
      return {};
    }
  })();
  const iaYear = String(iaUser.selectedYear ?? iaUser.year ?? iaUser.years ?? '').trim();
  const yearOptions = Array.isArray(iaUser.availableYears) && iaUser.availableYears.length > 0
    ? iaUser.availableYears
    : (iaYear ? [iaYear] : []);
  const [selectedYear, setSelectedYear] = useState(() => iaYear || yearOptions[0] || '');
  const activeYear = String(selectedYear || iaYear || yearOptions[0] || '').trim();
  const apiBase = (process.env.REACT_APP_API_BASE_URL || process.env.REACT_APP_API_URL || 'http://localhost:5000').replace(/\/+$/, '');

  console.log('[DashboardIA] Year configuration:', {
    iaYear,
    yearOptions,
    selectedYear,
    activeYear,
  });

  const handleYearSelectionChange = (year) => {
    console.log('[handleYearSelectionChange] Year changed from', selectedYear, 'to', year);
    setSelectedYear(year);
    setMembersPage(1);
    setMachineryPage(1);
  };

  const toNumberSafe = (value) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  };

  const buildOverviewAnalytics = (membersData, machineriesData) => {
    const totalMembers = membersData.length;
    const totalMachineries = machineriesData.length;

    let maleMembers = 0;
    let femaleMembers = 0;
    let unspecifiedMembers = 0;
    let farmAreaTotal = 0;
    let farmAreaCount = 0;
    let rsbsaRegisteredCount = 0;

    membersData.forEach((member) => {
      const sex = String(member?.sex ?? '').trim().toLowerCase();
      if (sex === 'male' || sex === 'm') {
        maleMembers += 1;
      } else if (sex === 'female' || sex === 'f') {
        femaleMembers += 1;
      } else {
        unspecifiedMembers += 1;
      }

      const farmArea = toNumberSafe(member?.farmArea);
      if (farmArea > 0) {
        farmAreaTotal += farmArea;
        farmAreaCount += 1;
      }

      const rsbsaValue = String(member?.rsbsa_registered ?? '').trim().toLowerCase();
      if (['yes', 'y', 'true', '1', 'registered'].includes(rsbsaValue)) {
        rsbsaRegisteredCount += 1;
      }
    });

    let machineryFunctionalUnits = 0;
    let machineryNotFunctionalUnits = 0;
    const acquisitionCounter = new Map();

    machineriesData.forEach((machinery) => {
      machineryFunctionalUnits += toNumberSafe(machinery?.functional);
      machineryNotFunctionalUnits += toNumberSafe(machinery?.not_functional);

      const mode = String(machinery?.mode_acquisition ?? '').trim() || 'Unspecified';
      acquisitionCounter.set(mode, (acquisitionCounter.get(mode) || 0) + 1);
    });

    const acquisitionBreakdown = Array.from(acquisitionCounter.entries())
      .map(([label, count]) => ({ label, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 5);

    return {
      totalMembers,
      totalMachineries,
      maleMembers,
      femaleMembers,
      unspecifiedMembers,
      averageFarmArea: farmAreaCount > 0 ? farmAreaTotal / farmAreaCount : 0,
      rsbsaRegisteredCount,
      machineryFunctionalUnits,
      machineryNotFunctionalUnits,
      acquisitionBreakdown,
    };
  };
  const memberTableColumns = [
    { key: 'id', label: 'ID' },
    { key: 'years', label: 'Year' },
    { key: 'is_name', label: 'IS Name' },
    { key: 'system_type', label: 'System Type' },
    { key: 'ia_name', label: 'IA Name' },
    { key: 'ia_region', label: 'IA Region' },
    { key: 'ia_province', label: 'IA Province' },
    { key: 'ia_municipality', label: 'IA Municipality' },
    { key: 'ia_barangay', label: 'IA Barangay' },
    { key: 'lastname', label: 'Last Name' },
    { key: 'firstname', label: 'First Name' },
    { key: 'middlename', label: 'Middle Name' },
    { key: 'extensionName', label: 'Extension Name' },
    { key: 'sex', label: 'Sex' },
    { key: 'birthday', label: 'Birthday' },
    { key: 'mothers_maidenName', label: 'Mother Maiden Name' },
    { key: 'spouse_lastname', label: 'Spouse Last Name' },
    { key: 'spouse_firstName', label: 'Spouse First Name' },
    { key: 'spouse_middleName', label: 'Spouse Middle Name' },
    { key: 'spouse_extName', label: 'Spouse Ext Name' },
    { key: 'region', label: 'Region' },
    { key: 'province', label: 'Province' },
    { key: 'municipality', label: 'Municipality' },
    { key: 'barangay', label: 'Barangay' },
    { key: 'houseNumber', label: 'House Number' },
    { key: 'regionFarm', label: 'Farm Region' },
    { key: 'provinceFarm', label: 'Farm Province' },
    { key: 'municipalityFarm', label: 'Farm Municipality' },
    { key: 'barangayFarm', label: 'Farm Barangay' },
    { key: 'farm_lotNum', label: 'Farm Lot Number' },
    { key: 'farm_lotCode', label: 'Farm Lot Code' },
    { key: 'farmArea', label: 'Farm Area' },
    { key: 'ownershipType', label: 'Ownership Type' },
    { key: 'rsbsa_registered', label: 'RSBSA Registered' },
    { key: 'da_imc', label: 'DA IMC' },
    { key: 'rsbsa_refNum', label: 'RSBSA Ref Num' },
    { key: 'rsbsa_controlNum', label: 'RSBSA Control Num' },
    { key: 'national_id', label: 'National ID' },
    { key: 'philsys_cardNum', label: 'PhilSys Card Num' },
    { key: 'transactionSlip', label: 'Transaction Slip' },
    { key: 'transDate', label: 'Transaction Date' },
  ];

  const iaManagementTableColumns = [
    { key: 'id', label: 'ID' },
    { key: 'is_name', label: 'IS Name' },
    { key: 'years', label: 'Years' },
    { key: 'ia_name', label: 'IA Name' },
    { key: 'cis_name', label: 'CIS Name' },
    { key: 'municipality', label: 'Municipality' },
    { key: 'districtNum', label: 'District No.' },
    { key: 'mailing_address', label: 'Mailing Address' },
    { key: 'ia_president', label: 'IA President' },
    { key: 'contactNum', label: 'Contact No.' },
    { key: 'dateOrganized', label: 'Date Organized' },
    { key: 'secNum', label: 'SEC No.' },
    { key: 'secDate', label: 'SEC Date' },
    { key: 'ia_tin', label: 'IA TIN' },
    { key: 'membership_potl', label: 'Membership Potential' },
    { key: 'membership_actual', label: 'Membership Actual' },
    { key: 'serviceArea', label: 'Service Area' },
    { key: 'fusa', label: 'FUSA' },
    { key: 'irrigated_area', label: 'Irrigated Area' },
    { key: 'operational_area', label: 'Operational Area' },
    { key: 'transdate', label: 'Transdate' },
  ];

  const profileFields = [
    {
      category: 'Basic Information',
      fields: [
        { key: 'id', label: 'ID' },
        { key: 'ia_name', label: 'IA Name' },
        { key: 'is_name', label: 'IS Name' },
        { key: 'years', label: 'Years' },
      ]
    },
    {
      category: 'Organization Details',
      fields: [
        { key: 'cis_name', label: 'CIS Name' },
        { key: 'ia_president', label: 'IA President' },
        { key: 'dateOrganized', label: 'Date Organized' },
      ]
    },
    {
      category: 'Location & Address',
      fields: [
        { key: 'municipality', label: 'Municipality' },
        { key: 'districtNum', label: 'District No.' },
        { key: 'mailing_address', label: 'Mailing Address' },
      ]
    },
    {
      category: 'Contact Information',
      fields: [
        { key: 'contactNum', label: 'Contact Number' },
      ]
    },
    {
      category: 'Regulatory & Legal',
      fields: [
        { key: 'secNum', label: 'SEC Number' },
        { key: 'secDate', label: 'SEC Date' },
        { key: 'ia_tin', label: 'IA TIN' },
      ]
    },
    {
      category: 'Membership',
      fields: [
        { key: 'membership_potl', label: 'Membership Potential' },
        { key: 'membership_actual', label: 'Membership Actual' },
      ]
    },
    {
      category: 'Service Area & Coverage',
      fields: [
        { key: 'serviceArea', label: 'Service Area' },
        { key: 'fusa', label: 'FUSA' },
        { key: 'irrigated_area', label: 'Irrigated Area' },
        { key: 'operational_area', label: 'Operational Area' },
      ]
    },
    {
      category: 'Administrative',
      fields: [
        { key: 'transdate', label: 'Transdate' },
      ]
    },
  ];

  const editableMemberFields = [
    { key: 'lastname', label: 'Last Name' },
    { key: 'firstname', label: 'First Name' },
    { key: 'middlename', label: 'Middle Name' },
    { key: 'extensionName', label: 'Extension Name' },
    { key: 'sex', label: 'Sex' },
    { key: 'birthday', label: 'Birthday' },
    { key: 'mothers_maidenName', label: 'Mother Maiden Name' },
    { key: 'spouse_lastname', label: 'Spouse Last Name' },
    { key: 'spouse_firstName', label: 'Spouse First Name' },
    { key: 'spouse_middleName', label: 'Spouse Middle Name' },
    { key: 'spouse_extName', label: 'Spouse Extension Name' },
    { key: 'region', label: 'Region' },
    { key: 'province', label: 'Province' },
    { key: 'municipality', label: 'Municipality' },
    { key: 'barangay', label: 'Barangay' },
    { key: 'houseNumber', label: 'House Number' },
    { key: 'regionFarm', label: 'Farm Region' },
    { key: 'provinceFarm', label: 'Farm Province' },
    { key: 'municipalityFarm', label: 'Farm Municipality' },
    { key: 'barangayFarm', label: 'Farm Barangay' },
    { key: 'farm_lotNum', label: 'Farm Lot Number' },
    { key: 'farm_lotCode', label: 'Farm Lot Code' },
    { key: 'farmArea', label: 'Farm Area', type: 'number' },
    { key: 'ownershipType', label: 'Ownership Type' },
    { key: 'rsbsa_registered', label: 'RSBSA Registered' },
    { key: 'da_imc', label: 'DA IMC' },
    { key: 'rsbsa_refNum', label: 'RSBSA Ref Number' },
    { key: 'rsbsa_controlNum', label: 'RSBSA Control Number' },
    { key: 'national_id', label: 'National ID' },
    { key: 'philsys_cardNum', label: 'PhilSys Card Number' },
    { key: 'transactionSlip', label: 'Transaction Slip' },
  ];

  const addMemberSections = [
    {
      title: 'Session Details',
      fields: [
        { key: 'years', label: 'Year' },
        { key: 'is_name', label: 'IS Name' },
        { key: 'ia_name', label: 'IA Name' },
      ],
    },
    {
      title: 'IA Details',
      fields: [
        { key: 'system_type', label: 'System Type' },
        { key: 'ia_region', label: 'IA Region' },
        { key: 'ia_province', label: 'IA Province' },
        { key: 'ia_municipality', label: 'IA Municipality' },
        { key: 'ia_barangay', label: 'IA Barangay' },
      ],
    },
    {
      title: 'Member Personal Information',
      fields: editableMemberFields.filter((field) =>
        ['lastname', 'firstname', 'middlename', 'extensionName', 'sex', 'birthday', 'mothers_maidenName'].includes(field.key)
      ),
    },
    {
      title: 'Spouse Information',
      fields: editableMemberFields.filter((field) =>
        ['spouse_lastname', 'spouse_firstName', 'spouse_middleName', 'spouse_extName'].includes(field.key)
      ),
    },
    {
      title: 'Address',
      fields: editableMemberFields.filter((field) =>
        ['region', 'province', 'municipality', 'barangay', 'houseNumber'].includes(field.key)
      ),
    },
    {
      title: 'Farm Details',
      fields: editableMemberFields.filter((field) =>
        ['regionFarm', 'provinceFarm', 'municipalityFarm', 'barangayFarm', 'farm_lotNum', 'farm_lotCode', 'farmArea', 'ownershipType'].includes(field.key)
      ),
    },
    {
      title: 'IDs and Registration',
      fields: editableMemberFields.filter((field) =>
        ['rsbsa_registered', 'da_imc', 'rsbsa_refNum', 'rsbsa_controlNum', 'national_id', 'philsys_cardNum', 'transactionSlip'].includes(field.key)
      ),
    },
  ];

  const machineryTableColumns = [
    { key: 'id', label: 'ID' },
    { key: 'machineriesEquipment', label: 'Equipment' },
    { key: 'barangay', label: 'Barangay' },
    { key: 'municipality', label: 'Municipality' },
    { key: 'province', label: 'Province' },
    { key: 'no_unit', label: 'No. of Units' },
    { key: 'year_acquired', label: 'Year Acquired' },
    { key: 'functional', label: 'Functional' },
    { key: 'not_functional', label: 'Not Functional' },
    { key: 'mode_acquisition', label: 'Mode of Acquisition' },
    { key: 'partner_shareholder', label: 'Partner/Shareholder' },
    { key: 'yes_Reg_num', label: 'Registration #' },
    { key: 'transDate', label: 'Transaction Date' },
  ];

  const editableMachineryFields = [
    { key: 'machineriesEquipment', label: 'Equipment' },
    { key: 'barangay', label: 'Barangay' },
    { key: 'municipality', label: 'Municipality' },
    { key: 'province', label: 'Province' },
    { key: 'no_unit', label: 'No. of Units' },
    { key: 'year_acquired', label: 'Year Acquired' },
    { key: 'functional', label: 'Functional' },
    { key: 'not_functional', label: 'Not Functional' },
    { key: 'mode_acquisition', label: 'Mode of Acquisition' },
    { key: 'partner_shareholder', label: 'Partner/Shareholder' },
    { key: 'yes_Reg_num', label: 'Registration #' },
    { key: 'no_Reg_num', label: 'Non-Registration #' },
    { key: 'transDate', label: 'Transaction Date' },
  ];

  useEffect(() => {
    const token = localStorage.getItem('iaToken');
    const role = localStorage.getItem('iaRole');
    if (!token || role !== 'ia') {
      navigate('/LoginIA', { replace: true });
    }
  }, [navigate]);

  // Fetch sister IAs
  const fetchSisterIAs = async () => {
    setLoading(true);
    try {
      const url = `${apiBase}/nis/view-sister-ias?is_name=${encodeURIComponent(
        iaUser.is_name
      )}&year=${iaYear}&own_ia_name=${encodeURIComponent(iaUser.ia_name)}`;
      const response = await fetch(url);
      const json = await response.json();
      if (!response.ok) {
        const message = json && json.message ? json.message : 'Failed to fetch sister IAs';
        throw new Error(message);
      }
      const sisterData = Array.isArray(json?.data) ? json.data : Array.isArray(json) ? json : [];
      setSisterIAs(sisterData);
    } catch (err) {
      console.error('Error fetching sister IAs:', err);
      toast.error(err.message || 'Failed to load sister IAs');
    } finally {
      setLoading(false);
    }
  };

  // Fetch members
  const fetchMembers = async () => {
    // Guard: don't fetch if required parameters are missing
    if (!iaUser.ia_name || !iaUser.is_name || !activeYear) {
      console.warn('[fetchMembers] Skipping fetch: missing parameters', {
        ia_name: iaUser.ia_name,
        is_name: iaUser.is_name,
        year: activeYear,
      });
      setMembers([]);
      return;
    }

    setLoading(true);
    try {
      const url = `${apiBase}/api/nisia/get-members?ia_name=${encodeURIComponent(
        iaUser.ia_name
      )}&is_name=${encodeURIComponent(iaUser.is_name)}&year=${activeYear}`;
      console.log('[fetchMembers] Fetching with URL:', url, 'activeYear:', activeYear);
      const response = await fetch(url);
      const json = await response.json();
      console.log('[fetchMembers] Response:', json);
      if (!response.ok) {
        const message = json && json.message ? json.message : 'Failed to fetch members';
        throw new Error(message);
      }
      const memberData = Array.isArray(json?.data)
        ? json.data
        : Array.isArray(json?.members)
          ? json.members
          : Array.isArray(json)
            ? json
            : [];
      console.log('[fetchMembers] Filtered data count:', memberData.length, 'for year:', activeYear);
      setMembers(memberData);
    } catch (err) {
      console.error('Error fetching members:', err);
      toast.error(err.message || 'Failed to load members');
    } finally {
      setLoading(false);
    }
  };

  // Fetch overview analytics (profile + members + machineries)
  const fetchOverviewAnalytics = async () => {
    setOverviewLoading(true);
    try {
      const profileUrl = `${apiBase}/api/nisia/profile?ia_name=${encodeURIComponent(
        iaUser.ia_name
      )}&is_name=${encodeURIComponent(iaUser.is_name)}&year=${iaYear}`;
      const membersUrl = `${apiBase}/api/nisia/get-members?ia_name=${encodeURIComponent(
        iaUser.ia_name
      )}&is_name=${encodeURIComponent(iaUser.is_name)}&year=${iaYear}`;
      const machineriesUrl = `${apiBase}/api/iamachineries/get?ia_name=${encodeURIComponent(
        iaUser.ia_name
      )}&is_name=${encodeURIComponent(iaUser.is_name)}&year=${iaYear}`;

      const [profileResponse, membersResponse, machineriesResponse] = await Promise.all([
        fetch(profileUrl),
        fetch(membersUrl),
        fetch(machineriesUrl),
      ]);

      const [profileJson, membersJson, machineriesJson] = await Promise.all([
        profileResponse.json(),
        membersResponse.json(),
        machineriesResponse.json(),
      ]);

      if (!profileResponse.ok) {
        throw new Error(profileJson?.message || 'Failed to fetch IA profile');
      }
      if (!membersResponse.ok) {
        throw new Error(membersJson?.message || 'Failed to fetch members');
      }
      if (!machineriesResponse.ok) {
        throw new Error(machineriesJson?.message || 'Failed to fetch machineries');
      }

      const profileData = profileJson?.data && typeof profileJson.data === 'object'
        ? profileJson.data
        : Array.isArray(profileJson?.data)
          ? profileJson.data[0] || null
          : null;

      const membersData = Array.isArray(membersJson?.data)
        ? membersJson.data
        : Array.isArray(membersJson?.members)
          ? membersJson.members
          : Array.isArray(membersJson)
            ? membersJson
            : [];

      const machineriesData = Array.isArray(machineriesJson?.data)
        ? machineriesJson.data
        : Array.isArray(machineriesJson)
          ? machineriesJson
          : [];

      setProfile(profileData);
      setProfileLoaded(true);
      setMembers(membersData);
      setMachineries(machineriesData);
      setOverviewAnalytics(buildOverviewAnalytics(membersData, machineriesData));
      setOverviewLoaded(true);
    } catch (err) {
      console.error('Error loading overview analytics:', err);
      toast.error(err.message || 'Failed to load overview analytics');
      setOverviewLoaded(true);
    } finally {
      setOverviewLoading(false);
    }
  };

  // Fetch IA profile
  const fetchProfile = async () => {
    setLoading(true);
    try {
      const url = `${apiBase}/api/nisia/profile?ia_name=${encodeURIComponent(
        iaUser.ia_name
      )}&is_name=${encodeURIComponent(iaUser.is_name)}&year=${iaYear}`;
      const response = await fetch(url);
      const json = await response.json();
      if (!response.ok) {
        const message = json && json.message ? json.message : 'Failed to fetch IA profile';
        throw new Error(message);
      }

      const profileData = json?.data && typeof json.data === 'object'
        ? json.data
        : Array.isArray(json?.data)
          ? json.data[0] || null
          : null;

      setProfile(profileData);
      setProfileLoaded(true);
    } catch (err) {
      console.error('Error fetching IA profile:', err);
      toast.error(err.message || 'Failed to load IA profile');
      setProfileLoaded(true);
    } finally {
      setLoading(false);
    }
  };

  // Fetch machineries
  const fetchMachineries = async () => {
    // Guard: don't fetch if required parameters are missing
    if (!iaUser.ia_name || !iaUser.is_name || !activeYear) {
      console.warn('[fetchMachineries] Skipping fetch: missing parameters', {
        ia_name: iaUser.ia_name,
        is_name: iaUser.is_name,
        year: activeYear,
      });
      setMachineries([]);
      return;
    }

    setLoading(true);
    try {
      console.log('[fetchMachineries] Fetching with parameters:', {
        ia_name: iaUser.ia_name,
        is_name: iaUser.is_name,
        year: activeYear,
      });
      const url = `${apiBase}/api/iamachineries/get?ia_name=${encodeURIComponent(
        iaUser.ia_name
      )}&is_name=${encodeURIComponent(iaUser.is_name)}&year=${activeYear}`;
      console.log('[fetchMachineries] Request URL:', url);
      const response = await fetch(url);
      const json = await response.json();
      console.log('[fetchMachineries] Response data:', json);
      if (!response.ok) {
        const message = json && json.message ? json.message : 'Failed to fetch machineries';
        throw new Error(message);
      }
      const machineryData = Array.isArray(json?.data) ? json.data : Array.isArray(json) ? json : [];
      console.log('[fetchMachineries] Filtered data count:', machineryData.length, 'for year:', activeYear);
      setMachineries(machineryData);
    } catch (err) {
      console.error('[fetchMachineries] Error:', err);
      toast.error(err.message || 'Failed to load machineries');
    } finally {
      setLoading(false);
    }
  };

  // Handle tab change and fetch data
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    // reset paging when switching tabs
    setSisterPage(1);
    setMembersPage(1);
    setMachineryPage(1);
  };

  // ensure fetching when tab becomes active (covers direct navigation)
  useEffect(() => {
    console.log('[useEffect activeTab] Tab changed to:', activeTab, 'activeYear:', activeYear);
    if (activeTab === 'overview' && !overviewLoaded) fetchOverviewAnalytics();
    if (activeTab === 'ia-management' && sisterIAs.length === 0) fetchSisterIAs();
    if (activeTab === 'members' && activeYear) fetchMembers();
    if (activeTab === 'machineries' && activeYear) fetchMachineries();
    if (activeTab === 'profile' && !profileLoaded) fetchProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, activeYear]);

  useEffect(() => {
    console.log('[useEffect selectedYear] Year changed to:', selectedYear, 'activeYear:', activeYear);
    if (activeYear) {
      if (activeTab === 'members') {
        fetchMembers();
      }
      if (activeTab === 'machineries') {
        fetchMachineries();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedYear, activeYear]);

  // Pagination helpers
  const paginatedSisterIAs = (() => {
    const normalizedSearch = sisterSearchTerm.trim().toLowerCase();
    const filteredIAs = normalizedSearch
      ? sisterIAs.filter((ia) => {
          const iaName = String(ia.ia_name ?? '').toLowerCase();
          return iaName.includes(normalizedSearch);
        })
      : sisterIAs;

    const total = filteredIAs.length;
    const totalPages = Math.max(1, Math.ceil(total / pageSize));
    const page = Math.min(Math.max(1, sisterPage), totalPages);
    const start = (page - 1) * pageSize;
    return {
      items: filteredIAs.slice(start, start + pageSize),
      page,
      totalPages,
      total,
    };
  })();

  // Scroll sync for wide tables (IA Management and Members) — mimic NIS template behavior
  useEffect(() => {
    const setups = [
      { contentId: 'scrollMembers', bottomId: 'bottomScrollMembers' },
    ];

    const listeners = [];

    const updateBottomWidth = (contentEl, bottomEl) => {
      try {
        const innerTable = contentEl.querySelector('table');
        const width = innerTable ? innerTable.scrollWidth : contentEl.scrollWidth;
        const spacer = bottomEl.firstElementChild;
        if (spacer) spacer.style.width = width + 'px';
      } catch (e) {
        // ignore
      }
    };

    setups.forEach(({ contentId, bottomId }) => {
      const content = document.getElementById(contentId);
      const bottom = document.getElementById(bottomId);
      if (!content || !bottom) return;

      updateBottomWidth(content, bottom);

      const onBottomScroll = () => {
        content.scrollLeft = bottom.scrollLeft;
      };
      const onContentScroll = () => {
        bottom.scrollLeft = content.scrollLeft;
      };
      const onResize = () => updateBottomWidth(content, bottom);

      bottom.addEventListener('scroll', onBottomScroll);
      content.addEventListener('scroll', onContentScroll);
      window.addEventListener('resize', onResize);

      listeners.push(() => {
        bottom.removeEventListener('scroll', onBottomScroll);
        content.removeEventListener('scroll', onContentScroll);
        window.removeEventListener('resize', onResize);
      });
    });

    return () => listeners.forEach((fn) => fn());
  }, [members, sisterIAs]);

  const paginatedMembers = (() => {
    const normalizedSearch = memberSearchTerm.trim().toLowerCase();
    const filteredMembers = normalizedSearch
      ? members.filter((member) => {
          const searchableValues = [
            member.municipality,
            member.barangay,
            member.lastname,
            member.firstname,
            member.middlename,
          ]
            .map((value) => String(value ?? '').toLowerCase())
            .join(' ');

          return searchableValues.includes(normalizedSearch);
        })
      : members;

    const total = filteredMembers.length;
    const totalPages = Math.max(1, Math.ceil(total / pageSize));
    const page = Math.min(Math.max(1, membersPage), totalPages);
    const start = (page - 1) * pageSize;
    return {
      items: filteredMembers.slice(start, start + pageSize),
      page,
      totalPages,
      total,
    };
  })();

  const paginatedMachineries = (() => {
    const normalizedSearch = machinerySearchTerm.trim().toLowerCase();
    const filteredMachineries = normalizedSearch
      ? machineries.filter((machinery) => {
          const searchableValues = [
            machinery.machineriesEquipment,
            machinery.barangay,
            machinery.municipality,
            machinery.province,
          ]
            .map((value) => String(value ?? '').toLowerCase())
            .join(' ');

          return searchableValues.includes(normalizedSearch);
        })
      : machineries;

    const total = filteredMachineries.length;
    const totalPages = Math.max(1, Math.ceil(total / pageSize));
    const page = Math.min(Math.max(1, machineryPage), totalPages);
    const start = (page - 1) * pageSize;
    return {
      items: filteredMachineries.slice(start, start + pageSize),
      page,
      totalPages,
      total,
    };
  })();

  useEffect(() => {
    setSisterPage(1);
  }, [sisterSearchTerm]);

  useEffect(() => {
    setMembersPage(1);
  }, [memberSearchTerm]);

  useEffect(() => {
    setMachineryPage(1);
  }, [machinerySearchTerm]);

  const getColMinWidth = (key) => {
    switch (key) {
      case 'id': return 50;
      case 'is_name': return 140;
      case 'years': return 60;
      case 'ia_name': return 180;
      case 'cis_name': return 180;
      case 'municipality': return 110;
      case 'districtNum': return 100;
      case 'mailing_address': return 220;
      case 'ia_president': return 180;
      case 'contactNum': return 120;
      case 'dateOrganized': return 120;
      case 'secNum': return 130;
      case 'secDate': return 120;
      case 'ia_tin': return 140;
      case 'membership_potl':
      case 'membership_actual': return 130;
      case 'serviceArea':
      case 'fusa':
      case 'irrigated_area':
      case 'operational_area': return 120;
      case 'transdate': return 160;
      default: return 100;
    }
  };

  // Format dates to YYYY-MM-DD for display for specific keys (transdate / transDate)
  const formatDateISO = (value) => {
    if (value === null || value === undefined || value === '') return '';
    // If already in YYYY-MM-DD (or starts with it), return first 10 chars
    try {
      const s = String(value);
      const isoMatch = s.match(/^(\d{4}-\d{2}-\d{2})/);
      if (isoMatch) return isoMatch[1];
      // Try to parse using Date
      const d = new Date(s);
      if (!isNaN(d.getTime())) {
        return d.toISOString().slice(0, 10);
      }
    } catch (e) {
      // fallthrough
    }
    return String(value);
  };

  const formatFieldValue = (key, value) => {
    if (!key) return value ?? '';
    if (/transdate/i.test(key)) return formatDateISO(value);
    return value ?? '';
  };

  const startEditingProfile = () => {
    setEditProfileData(profile ? { ...profile } : {});
    setIsEditingProfile(true);
  };

  const closeEditingProfile = () => {
    setIsEditingProfile(false);
    setEditProfileData(null);
  };

  const handleEditProfileChange = (key, value) => {
    setEditProfileData((prev) => ({ ...(prev || {}), [key]: value }));
  };

  const saveProfile = async () => {
    if (!editProfileData) return;
    setProfileSaving(true);
    try {
      const payload = {
        ia_name: editProfileData.ia_name || profile?.ia_name,
        is_name: editProfileData.is_name || profile?.is_name,
        year: editProfileData.years || profile?.years || profile?.year,
        cis_name: editProfileData.cis_name || null,
        municipality: editProfileData.municipality || null,
        districtNum: editProfileData.districtNum || null,
        mailing_address: editProfileData.mailing_address || null,
        ia_president: editProfileData.ia_president || null,
        contactNum: editProfileData.contactNum || null,
        dateOrganized: editProfileData.dateOrganized || null,
        secNum: editProfileData.secNum || null,
        secDate: editProfileData.secDate || null,
        ia_tin: editProfileData.ia_tin || null,
        membership_potl: editProfileData.membership_potl ?? null,
        membership_actual: editProfileData.membership_actual ?? null,
        serviceArea: editProfileData.serviceArea ?? null,
        fusa: editProfileData.fusa ?? null,
        irrigated_area: editProfileData.irrigated_area ?? null,
        operational_area: editProfileData.operational_area ?? null
      };

      const resp = await fetch(`${apiBase}/api/nisia/profile`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const ct = resp.headers.get('content-type') || '';
      let json;
      if (ct.includes('application/json')) {
        json = await resp.json();
      } else {
        const text = await resp.text();
        throw new Error(text || 'Unexpected non-JSON response from server');
      }

      if (!resp.ok) throw new Error(json?.message || 'Failed to update');

      // update local profile with returned data
      setProfile(json.data || editProfileData);
      setIsEditingProfile(false);
      setEditProfileData(null);
      setSuccessMessage('Profile updated');
    } catch (err) {
      console.error('Error updating profile', err);
      window.alert('Error updating profile: ' + (err.message || err));
    } finally {
      setProfileSaving(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('iaToken');
    localStorage.removeItem('iaRole');
    localStorage.removeItem('iaCode');
    localStorage.removeItem('iaUser');
    navigate('/Home', { replace: true });
  };

  const startEditingMember = (member) => {
    setEditingMemberId(member.id);
    setEditFormData({ ...member });
  };

  const cancelEditing = () => {
    setEditingMemberId(null);
    setEditFormData({});
  };

  const handleMemberFieldChange = (field, value) => {
    setEditFormData((prev) => ({ ...prev, [field]: value }));
  };

  const buildMemberDefaults = () => {
    return {
      years: activeYear,
      is_name: iaUser.is_name || '',
      ia_name: iaUser.ia_name || '',
      system_type: '',
      ia_region: '',
      ia_province: '',
      ia_municipality: '',
      ia_barangay: '',
      lastname: '',
      firstname: '',
      middlename: '',
      extensionName: '',
      sex: '',
      birthday: '',
      mothers_maidenName: '',
      spouse_lastname: '',
      spouse_firstName: '',
      spouse_middleName: '',
      spouse_extName: '',
      region: '',
      province: '',
      municipality: '',
      barangay: '',
      houseNumber: '',
      regionFarm: '',
      provinceFarm: '',
      municipalityFarm: '',
      barangayFarm: '',
      farm_lotNum: '',
      farm_lotCode: '',
      farmArea: '',
      ownershipType: '',
      rsbsa_registered: '',
      da_imc: '',
      rsbsa_refNum: '',
      rsbsa_controlNum: '',
      national_id: '',
      philsys_cardNum: '',
      transactionSlip: '',
    };
  };

  const openAddMember = () => {
    setNewMemberData(buildMemberDefaults());
    setIsAddMemberOpen(true);
  };

  const closeAddMember = () => {
    setIsAddMemberOpen(false);
    setNewMemberData({});
  };

  const handleNewMemberFieldChange = (field, value) => {
    setNewMemberData((prev) => ({ ...prev, [field]: value }));
  };

  const saveNewMember = async () => {
    try {
      const payload = {
        ...newMemberData,
        years: activeYear,
        is_name: newMemberData.is_name || iaUser.is_name,
        ia_name: newMemberData.ia_name || iaUser.ia_name,
      };

      const response = await fetch(`${apiBase}/api/nisia/add-member`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      const json = await response.json();
      if (!response.ok) {
        throw new Error(json?.message || 'Failed to add member');
      }

      setSuccessMessage('Member added successfully');
      closeAddMember();
      await fetchMembers();
    } catch (err) {
      console.error('Error adding member:', err);
      toast.error(err.message || 'Failed to add member');
    }
  };

  const saveMemberUpdate = async () => {
    try {
      const payload = {
        id: editFormData.id,
        ia_name: iaUser.ia_name,
        is_name: iaUser.is_name,
        year: activeYear,
        ...editFormData,
      };

      const response = await fetch(
        `${apiBase}/api/nisia/update-member`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        }
      );

      if (!response.ok) throw new Error('Failed to update member');
      setSuccessMessage('Member updated successfully');
      setEditingMemberId(null);
      setEditFormData({});
      await fetchMembers();
    } catch (err) {
      console.error('Error updating member:', err);
      toast.error('Failed to update member');
    }
  };

  const startEditingMachinery = (machinery) => {
    setEditingMachineryId(machinery.id);
    setEditMachineryData({ ...machinery });
  };

  const openAddMachinery = () => {
    setNewMachineryData({
      years: activeYear,
      is_name: iaUser.is_name || '',
      ia_name: iaUser.ia_name || '',
      barangay: '',
      municipality: '',
      province: '',
      machineriesEquipment: '',
      no_unit: '',
      year_acquired: '',
      functional: '',
      not_functional: '',
      mode_acquisition: '',
      partner_shareholder: '',
      yes_Reg_num: '',
      no_Reg_num: '',
      transDate: '',
    });
    setIsAddMachineryOpen(true);
  };

  const closeAddMachinery = () => {
    setIsAddMachineryOpen(false);
    setNewMachineryData({});
  };

  const cancelEditingMachinery = () => {
    setEditingMachineryId(null);
    setEditMachineryData({});
  };

  const handleMachineryFieldChange = (field, value) => {
    setEditMachineryData((prev) => ({ ...prev, [field]: value }));
  };

  const handleNewMachineryFieldChange = (field, value) => {
    setNewMachineryData((prev) => ({ ...prev, [field]: value }));
  };

  const saveNewMachinery = async () => {
    try {
      const payload = {
        ...newMachineryData,
        years: activeYear,
        is_name: iaUser.is_name,
        ia_name: iaUser.ia_name,
      };

      const response = await fetch(`${apiBase}/api/iamachineries/add`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      const json = await response.json();
      if (!response.ok) {
        throw new Error(json?.message || 'Failed to add machinery');
      }

      setSuccessMessage('Machinery added successfully');
      closeAddMachinery();
      await fetchMachineries();
    } catch (err) {
      console.error('Error adding machinery:', err);
      toast.error(err.message || 'Failed to add machinery');
    }
  };

  const saveMachineryUpdate = async () => {
    try {
      const payload = {
        id: editMachineryData.id,
        ia_name: iaUser.ia_name,
        is_name: iaUser.is_name,
        year: activeYear,
        ...editMachineryData,
      };

      const response = await fetch(
        `${apiBase}/api/iamachineries/update`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        }
      );

      if (!response.ok) throw new Error('Failed to update machinery');
      setSuccessMessage('Machinery updated successfully');
      setEditingMachineryId(null);
      setEditMachineryData({});
      await fetchMachineries();
    } catch (err) {
      console.error('Error updating machinery:', err);
      toast.error('Failed to update machinery');
    }
  };

  // ============================================================
  // IMPORT/EXPORT FUNCTIONS
  // ============================================================

  const handleImportMembers = async (file) => {
    if (!file) return;

    // Validate required session values before sending
    if (!iaUser?.ia_name || !iaUser?.is_name) {
      toast.error('IA name and IS name are required for import');
      return;
    }

    setIsImportingMembers(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('ia_name', iaUser.ia_name);
      formData.append('is_name', iaUser.is_name);

      const response = await fetch(`${apiBase}/api/imexport/masterlist/import`, {
        method: 'POST',
        body: formData,
      });

      let json = {};
      try { json = await response.json(); } catch (e) { json = {}; }
      if (!response.ok) {
        const errMsg = json?.message || (json?.error || 'Failed to import members');
        throw new Error(errMsg);
      }

      const { results } = json;
      setSuccessMessage(
        `Import completed: ${results.inserted} inserted, ${results.skipped} skipped${
          results.errors.length > 0 ? `, ${results.errors.length} errors` : ''
        }`
      );
      await fetchMembers();
      memberFileInputRef.current.value = '';
    } catch (err) {
      console.error('Error importing members:', err);
      toast.error(err.message || 'Failed to import members');
    } finally {
      setIsImportingMembers(false);
    }
  };

  const handleExportMembers = async () => {
    setIsExportingMembers(true);
    try {
      const params = new URLSearchParams({
        ia_name: iaUser.ia_name,
        is_name: iaUser.is_name,
        year: activeYear,
      });

      const response = await fetch(`${apiBase}/api/imexport/masterlist/export?${params}`, {
        method: 'GET',
      });

      if (!response.ok) {
        const json = await response.json();
        throw new Error(json?.message || 'Failed to export members');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `masterlist_${iaUser.ia_name}_${activeYear}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      setSuccessMessage('Members exported successfully');
    } catch (err) {
      console.error('Error exporting members:', err);
      toast.error(err.message || 'Failed to export members');
    } finally {
      setIsExportingMembers(false);
    }
  };

  const handleImportMachineries = async (file) => {
    if (!file) return;

    // Validate required session values before sending
    if (!iaUser?.ia_name || !iaUser?.is_name || !activeYear) {
      toast.error('IA name, IS name, and year are required for import');
      return;
    }

    setIsImportingMachineries(true);
    try {
      console.debug('Importing machineries', { ia_name: iaUser.ia_name, is_name: iaUser.is_name, year: activeYear, fileName: file.name });
      const formData = new FormData();
      formData.append('file', file);
      formData.append('ia_name', iaUser.ia_name);
      formData.append('is_name', iaUser.is_name);
      formData.append('year', activeYear);

      const response = await fetch(`${apiBase}/api/imexport/machineries/import`, {
        method: 'POST',
        body: formData,
      });

      let json = {};
      try { json = await response.json(); } catch (e) { json = {}; }
      if (!response.ok) {
        const errMsg = json?.message || (json?.error || 'Failed to import machineries');
        throw new Error(errMsg);
      }

      const { results } = json;
      setSuccessMessage(
        `Import completed: ${results.inserted} inserted, ${results.skipped} skipped${
          results.errors.length > 0 ? `, ${results.errors.length} errors` : ''
        }`
      );
      await fetchMachineries();
      machineryFileInputRef.current.value = '';
    } catch (err) {
      console.error('Error importing machineries:', err);
      toast.error(err.message || 'Failed to import machineries');
    } finally {
      setIsImportingMachineries(false);
    }
  };

  const handleExportMachineries = async () => {
    setIsExportingMachineries(true);
    try {
      const params = new URLSearchParams({
        ia_name: iaUser.ia_name,
        is_name: iaUser.is_name,
        year: activeYear,
      });

      const response = await fetch(`${apiBase}/api/imexport/machineries/export?${params}`, {
        method: 'GET',
      });

      if (!response.ok) {
        const json = await response.json();
        throw new Error(json?.message || 'Failed to export machineries');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `machineries_${iaUser.ia_name}_${activeYear}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      setSuccessMessage('Machineries exported successfully');
    } catch (err) {
      console.error('Error exporting machineries:', err);
      toast.error(err.message || 'Failed to export machineries');
    } finally {
      setIsExportingMachineries(false);
    }
  };

  return (
    <Shell>
      <Container>
        <Sidebar>
          <SidebarHeader>
            <LogoContainer>
              <LogoImage src={NIALogo} alt="NIA Logo" />
            </LogoContainer>
            <Tag>IA Dashboard</Tag>
          </SidebarHeader>

          <NavTabs>
            <NavTab $active={activeTab === 'overview'} onClick={() => handleTabChange('overview')}>
              Overview
            </NavTab>
            <NavTab $active={activeTab === 'profile'} onClick={() => handleTabChange('profile')}>
              Profile
            </NavTab>
            <NavTab $active={activeTab === 'ia-management'} onClick={() => handleTabChange('ia-management')}>
              Explore
            </NavTab>
            <NavTab $active={activeTab === 'members'} onClick={() => handleTabChange('members')}>
              Members
            </NavTab>
            <NavTab $active={activeTab === 'machineries'} onClick={() => handleTabChange('machineries')}>
              Machineries
            </NavTab>
          </NavTabs>

          <LogoutSection>
            <LogoutButton onClick={handleLogout}>Logout</LogoutButton>
          </LogoutSection>
        </Sidebar>

        <MainContent>
          {successMessage && (
            <CustomizedSuccessCard
              message={successMessage}
              onClose={() => setSuccessMessage(null)}
            />
          )}
          {isEditingProfile && (
            <ModalOverlay onClick={closeEditingProfile}>
              <ModalDialog onClick={(e) => e.stopPropagation()}>
                <ModalHeader>
                  <ModalTitle>Edit IA Profile</ModalTitle>
                  <ModalCloseButton onClick={closeEditingProfile}>Close</ModalCloseButton>
                </ModalHeader>
                <EditPanel>
                  {/* Organization Details Section */}
                  <FieldSection>
                    <SectionTitle>Organization Details</SectionTitle>
                    <EditGrid>
                      <FieldGroup>
                        <FieldLabel>CIS Name</FieldLabel>
                        <Input value={editProfileData?.cis_name || ''} onChange={(e) => handleEditProfileChange('cis_name', e.target.value)} />
                      </FieldGroup>
                      <FieldGroup>
                        <FieldLabel>IA President</FieldLabel>
                        <Input value={editProfileData?.ia_president || ''} onChange={(e) => handleEditProfileChange('ia_president', e.target.value)} />
                      </FieldGroup>
                      <FieldGroup>
                        <FieldLabel>Date Organized</FieldLabel>
                        <Input value={editProfileData?.dateOrganized || ''} onChange={(e) => handleEditProfileChange('dateOrganized', e.target.value)} />
                      </FieldGroup>
                    </EditGrid>
                  </FieldSection>

                  {/* Location & Address Section */}
                  <FieldSection>
                    <SectionTitle>Location & Address</SectionTitle>
                    <EditGrid>
                      <FieldGroup>
                        <FieldLabel>Municipality</FieldLabel>
                        <Input value={editProfileData?.municipality || ''} onChange={(e) => handleEditProfileChange('municipality', e.target.value)} />
                      </FieldGroup>
                      <FieldGroup>
                        <FieldLabel>District No.</FieldLabel>
                        <Input value={editProfileData?.districtNum || ''} onChange={(e) => handleEditProfileChange('districtNum', e.target.value)} />
                      </FieldGroup>
                      <FieldGroup>
                        <FieldLabel>Mailing Address</FieldLabel>
                        <Input value={editProfileData?.mailing_address || ''} onChange={(e) => handleEditProfileChange('mailing_address', e.target.value)} />
                      </FieldGroup>
                    </EditGrid>
                  </FieldSection>

                  {/* Contact Information Section */}
                  <FieldSection>
                    <SectionTitle>Contact Information</SectionTitle>
                    <EditGrid>
                      <FieldGroup>
                        <FieldLabel>Contact No.</FieldLabel>
                        <Input value={editProfileData?.contactNum || ''} onChange={(e) => handleEditProfileChange('contactNum', e.target.value)} />
                      </FieldGroup>
                    </EditGrid>
                  </FieldSection>

                  {/* Regulatory & Legal Section */}
                  <FieldSection>
                    <SectionTitle>Regulatory & Legal</SectionTitle>
                    <EditGrid>
                      <FieldGroup>
                        <FieldLabel>Sec. No.</FieldLabel>
                        <Input value={editProfileData?.secNum || ''} onChange={(e) => handleEditProfileChange('secNum', e.target.value)} />
                      </FieldGroup>
                      <FieldGroup>
                        <FieldLabel>Sec. Date</FieldLabel>
                        <Input value={editProfileData?.secDate || ''} onChange={(e) => handleEditProfileChange('secDate', e.target.value)} />
                      </FieldGroup>
                      <FieldGroup>
                        <FieldLabel>IA TIN</FieldLabel>
                        <Input value={editProfileData?.ia_tin || ''} onChange={(e) => handleEditProfileChange('ia_tin', e.target.value)} />
                      </FieldGroup>
                    </EditGrid>
                  </FieldSection>

                  {/* Membership Section */}
                  <FieldSection>
                    <SectionTitle>Membership</SectionTitle>
                    <EditGrid>
                      <FieldGroup>
                        <FieldLabel>Membership (Potential)</FieldLabel>
                        <Input value={editProfileData?.membership_potl ?? ''} onChange={(e) => handleEditProfileChange('membership_potl', e.target.value)} />
                      </FieldGroup>
                      <FieldGroup>
                        <FieldLabel>Membership (Actual)</FieldLabel>
                        <Input value={editProfileData?.membership_actual ?? ''} onChange={(e) => handleEditProfileChange('membership_actual', e.target.value)} />
                      </FieldGroup>
                    </EditGrid>
                  </FieldSection>

                  {/* Service Area & Coverage Section */}
                  <FieldSection>
                    <SectionTitle>Service Area & Coverage</SectionTitle>
                    <EditGrid>
                      <FieldGroup>
                        <FieldLabel>Service Area</FieldLabel>
                        <Input value={editProfileData?.serviceArea ?? ''} onChange={(e) => handleEditProfileChange('serviceArea', e.target.value)} />
                      </FieldGroup>
                      <FieldGroup>
                        <FieldLabel>FUSA</FieldLabel>
                        <Input value={editProfileData?.fusa ?? ''} onChange={(e) => handleEditProfileChange('fusa', e.target.value)} />
                      </FieldGroup>
                      <FieldGroup>
                        <FieldLabel>Irrigated Area</FieldLabel>
                        <Input value={editProfileData?.irrigated_area ?? ''} onChange={(e) => handleEditProfileChange('irrigated_area', e.target.value)} />
                      </FieldGroup>
                      <FieldGroup>
                        <FieldLabel>Operational Area</FieldLabel>
                        <Input value={editProfileData?.operational_area ?? ''} onChange={(e) => handleEditProfileChange('operational_area', e.target.value)} />
                      </FieldGroup>
                    </EditGrid>
                  </FieldSection>

                  <EditActions>
                    <ActionButton $cancel onClick={closeEditingProfile}>Cancel</ActionButton>
                    <ActionButton $save onClick={saveProfile} disabled={profileSaving}>{profileSaving ? 'Saving...' : 'Save'}</ActionButton>
                  </EditActions>
                </EditPanel>
              </ModalDialog>
            </ModalOverlay>
          )}
          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <ContentSection>
              <ContentHeader>
                <Title>Overview Analytics</Title>
                <Subtitle>Performance snapshot for {iaUser.ia_name || 'an IA user'} in {iaYear || 'current year'}.</Subtitle>
              </ContentHeader>

              {overviewLoading ? (
                <LoadingMessage>Loading analytics...</LoadingMessage>
              ) : (
                <>
                  <StatsGrid>
                    <StatCard>
                      <StatLabel>Total Members</StatLabel>
                      <StatValue>{overviewAnalytics.totalMembers}</StatValue>
                    </StatCard>
                    <StatCard>
                      <StatLabel>Total Machineries</StatLabel>
                      <StatValue>{overviewAnalytics.totalMachineries}</StatValue>
                    </StatCard>
                    <StatCard>
                      <StatLabel>Avg Farm Area</StatLabel>
                      <StatValue>{overviewAnalytics.averageFarmArea.toFixed(2)}</StatValue>
                    </StatCard>
                    <StatCard>
                      <StatLabel>RSBSA Registered</StatLabel>
                      <StatValue>{overviewAnalytics.rsbsaRegisteredCount}</StatValue>
                    </StatCard>
                  </StatsGrid>

                  <AnalyticsGrid>
                    <ChartCard>
                      <ChartTitle>Member Sex Distribution</ChartTitle>
                      <MetricRow>
                        <MetricLabel>Male ({overviewAnalytics.maleMembers})</MetricLabel>
                        <MetricTrack>
                          <MetricFill
                            style={{
                              width: `${overviewAnalytics.totalMembers > 0
                                ? (overviewAnalytics.maleMembers / overviewAnalytics.totalMembers) * 100
                                : 0}%`,
                              background: 'linear-gradient(90deg, #2e7d32 0%, #55a630 100%)'
                            }}
                          />
                        </MetricTrack>
                      </MetricRow>
                      <MetricRow>
                        <MetricLabel>Female ({overviewAnalytics.femaleMembers})</MetricLabel>
                        <MetricTrack>
                          <MetricFill
                            style={{
                              width: `${overviewAnalytics.totalMembers > 0
                                ? (overviewAnalytics.femaleMembers / overviewAnalytics.totalMembers) * 100
                                : 0}%`,
                              background: 'linear-gradient(90deg, #1982c4 0%, #4cc9f0 100%)'
                            }}
                          />
                        </MetricTrack>
                      </MetricRow>
                      <MetricRow>
                        <MetricLabel>Unspecified ({overviewAnalytics.unspecifiedMembers})</MetricLabel>
                        <MetricTrack>
                          <MetricFill
                            style={{
                              width: `${overviewAnalytics.totalMembers > 0
                                ? (overviewAnalytics.unspecifiedMembers / overviewAnalytics.totalMembers) * 100
                                : 0}%`,
                              background: 'linear-gradient(90deg, #6c757d 0%, #adb5bd 100%)'
                            }}
                          />
                        </MetricTrack>
                      </MetricRow>
                    </ChartCard>

                    <ChartCard>
                      <ChartTitle>Machinery Health</ChartTitle>
                      <DonutWrap>
                        <DonutRing
                          style={{
                            background: `conic-gradient(
                              #2e7d32 0deg ${(overviewAnalytics.machineryFunctionalUnits + overviewAnalytics.machineryNotFunctionalUnits) > 0
                                ? (overviewAnalytics.machineryFunctionalUnits / (overviewAnalytics.machineryFunctionalUnits + overviewAnalytics.machineryNotFunctionalUnits)) * 360
                                : 0}deg,
                              #e76f51 ${(overviewAnalytics.machineryFunctionalUnits + overviewAnalytics.machineryNotFunctionalUnits) > 0
                                ? (overviewAnalytics.machineryFunctionalUnits / (overviewAnalytics.machineryFunctionalUnits + overviewAnalytics.machineryNotFunctionalUnits)) * 360
                                : 0}deg 360deg
                            )`
                          }}
                        >
                          <DonutCenter>
                            <DonutNumber>
                              {overviewAnalytics.machineryFunctionalUnits + overviewAnalytics.machineryNotFunctionalUnits}
                            </DonutNumber>
                            <DonutText>Units</DonutText>
                          </DonutCenter>
                        </DonutRing>
                      </DonutWrap>
                      <LegendRow>
                        <LegendSwatch style={{ background: '#2e7d32' }} />
                        <LegendText>Functional: {overviewAnalytics.machineryFunctionalUnits}</LegendText>
                      </LegendRow>
                      <LegendRow>
                        <LegendSwatch style={{ background: '#e76f51' }} />
                        <LegendText>Not Functional: {overviewAnalytics.machineryNotFunctionalUnits}</LegendText>
                      </LegendRow>
                    </ChartCard>

                    <ChartCard style={{ gridColumn: '1 / -1' }}>
                      <ChartTitle>Top Acquisition Modes</ChartTitle>
                      {overviewAnalytics.acquisitionBreakdown.length > 0 ? (
                        overviewAnalytics.acquisitionBreakdown.map((item) => (
                          <MetricRow key={item.label}>
                            <MetricLabel>{item.label} ({item.count})</MetricLabel>
                            <MetricTrack>
                              <MetricFill
                                style={{
                                  width: `${overviewAnalytics.totalMachineries > 0
                                    ? (item.count / overviewAnalytics.totalMachineries) * 100
                                    : 0}%`,
                                  background: 'linear-gradient(90deg, #1f6b2a 0%, #00a878 100%)'
                                }}
                              />
                            </MetricTrack>
                          </MetricRow>
                        ))
                      ) : (
                        <EmptyMessage>No acquisition data available.</EmptyMessage>
                      )}
                    </ChartCard>
                  </AnalyticsGrid>

                  <ProfileMiniCard>
                    <ProfileMiniTitle>IA Profile Quick View</ProfileMiniTitle>
                    <ProfileMiniText>
                      CIS: {profile?.cis_name || 'N/A'} | Municipality: {profile?.municipality || 'N/A'} | President: {profile?.ia_president || 'N/A'}
                    </ProfileMiniText>
                  </ProfileMiniCard>
                </>
              )}
            </ContentSection>
          )}

          {/* IA Management Tab */}
          {activeTab === 'ia-management' && (
            <ContentSection>
              <ContentHeader>
                <Title>IA Explore</Title>
                <Subtitle>Other Irrigation Associations under {iaUser.is_name}</Subtitle>
              </ContentHeader>

              {loading ? (
                <LoadingMessage>Loading sister IAs...</LoadingMessage>
              ) : sisterIAs.length > 0 ? (
                <>
                  <MembersToolbar>
                    <MembersSearchInput
                      type="text"
                      value={sisterSearchTerm}
                      onChange={(e) => setSisterSearchTerm(e.target.value)}
                      placeholder="Search IA Name"
                    />
                  </MembersToolbar>

                  <TableWrapper>
                    <div id="scrollSister" style={{ width: '100%', overflowX: 'auto' }}>
                      <Table>
                        <TableHead>
                          <TableRow>
                            {iaManagementTableColumns.map((column) => (
                              <TableHeader key={column.key} style={{ minWidth: getColMinWidth(column.key) }}>{column.label}</TableHeader>
                            ))}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {paginatedSisterIAs.items.map((ia) => (
                            <TableRow key={ia.id}>
                              {iaManagementTableColumns.map((col) => (
                                <TableCell key={`${ia.id}-${col.key}`} style={{ minWidth: getColMinWidth(col.key) }}>{formatFieldValue(col.key, ia[col.key])}</TableCell>
                              ))}
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </TableWrapper>

                  <PaginationContainer>
                    <PageButton
                      onClick={() => setSisterPage((p) => Math.max(1, p - 1))}
                      disabled={paginatedSisterIAs.page <= 1}
                    >
                      Prev
                    </PageButton>
                    <PageInfo>
                      Page {paginatedSisterIAs.page} of {paginatedSisterIAs.totalPages} ({paginatedSisterIAs.total} items)
                    </PageInfo>
                    <PageButton
                      onClick={() => setSisterPage((p) => Math.min(paginatedSisterIAs.totalPages, p + 1))}
                      disabled={paginatedSisterIAs.page >= paginatedSisterIAs.totalPages}
                    >
                      Next
                    </PageButton>
                  </PaginationContainer>
                </>
              ) : (
                <EmptyMessage>No sister IAs found.</EmptyMessage>
              )}
            </ContentSection>
          )}

          {/* Members Tab */}
          {activeTab === 'members' && (
            <ContentSection>
              <ContentHeader>
                <Title>Members Management</Title>
                <Subtitle>All members belonging to {iaUser.ia_name} for {activeYear || 'selected year'}</Subtitle>
              </ContentHeader>

              <MachineryToolbar>
                <ToolbarLeft>
                  <ActionButton $add onClick={openAddMember}>
                    Add Member
                  </ActionButton>
                  <ActionButton $add onClick={() => memberFileInputRef.current?.click()} disabled={isImportingMembers}>
                    {isImportingMembers ? 'Importing...' : 'Import'}
                  </ActionButton>
                  <ActionButton $add onClick={handleExportMembers} disabled={isExportingMembers}>
                    {isExportingMembers ? 'Exporting...' : 'Export'}
                  </ActionButton>
                  <input
                    ref={memberFileInputRef}
                    type="file"
                    accept=".xlsx,.xls,.csv"
                    style={{ display: 'none' }}
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) handleImportMembers(file);
                    }}
                  />
                </ToolbarLeft>
                <ToolbarRight>
                  <MembersSearchInput
                    type="text"
                    value={memberSearchTerm}
                    onChange={(e) => setMemberSearchTerm(e.target.value)}
                    placeholder="Search municipality, barangay, lastname, firstname, middlename"
                  />
                  <YearFilterGroup>
                    <YearFilterLabel htmlFor="members-year-filter"></YearFilterLabel>
                    <YearSelect
                      id="members-year-filter"
                      value={activeYear}
                      onChange={(e) => handleYearSelectionChange(e.target.value)}
                    >
                      {(yearOptions.length > 0 ? yearOptions : [iaYear]).filter(Boolean).map((year) => (
                        <option key={year} value={year}>{year}</option>
                      ))}
                    </YearSelect>
                  </YearFilterGroup>
                </ToolbarRight>
              </MachineryToolbar>

              {loading ? (
                <LoadingMessage>Loading members...</LoadingMessage>
              ) : members.length > 0 ? (
                <>
                  <TableWrapper>
                    <div id="scrollMembers" style={{ width: '100%', overflowX: 'auto' }}>
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableHeader style={{ minWidth: 100 }}>Action</TableHeader>
                            {memberTableColumns.map((column) => (
                              <TableHeader key={column.key} style={{ minWidth: getColMinWidth(column.key) }}>{column.label}</TableHeader>
                            ))}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {paginatedMembers.items.map((member) => {
                            return (
                              <TableRow key={member.id}>
                                <ActionCell>
                                  <ActionButton $edit onClick={() => startEditingMember(member)}>
                                    Edit
                                  </ActionButton>
                                </ActionCell>
                                {memberTableColumns.map((column) => (
                                  <TableCell key={`${member.id}-${column.key}`} style={{ minWidth: getColMinWidth(column.key) }}>
                                    {formatFieldValue(column.key, member[column.key])}
                                  </TableCell>
                                ))}
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </div>
                  </TableWrapper>

                  <PaginationContainer>
                    <PageButton
                      onClick={() => setMembersPage((p) => Math.max(1, p - 1))}
                      disabled={paginatedMembers.page <= 1}
                    >
                      Prev
                    </PageButton>
                    <PageInfo>
                      Page {paginatedMembers.page} of {paginatedMembers.totalPages} ({paginatedMembers.total} items)
                    </PageInfo>
                    <PageButton
                      onClick={() => setMembersPage((p) => Math.min(paginatedMembers.totalPages, p + 1))}
                      disabled={paginatedMembers.page >= paginatedMembers.totalPages}
                    >
                      Next
                    </PageButton>
                  </PaginationContainer>
                </>
              ) : (
                <EmptyMessage>No members found.</EmptyMessage>
              )}

              {isAddMemberOpen && (
                <ModalOverlay onClick={closeAddMember}>
                  <ModalDialog onClick={(e) => e.stopPropagation()}>
                    <ModalHeader>
                      <ModalTitle>Add Member Information</ModalTitle>
                      <ModalCloseButton type="button" onClick={closeAddMember}>
                        Close
                      </ModalCloseButton>
                    </ModalHeader>

                    {addMemberSections.map((section) => (
                      <FieldSection key={section.title}>
                        <SectionTitle>{section.title}</SectionTitle>
                        <EditGrid>
                          {section.fields.map((field) => (
                            <FieldGroup key={field.key}>
                              <FieldLabel htmlFor={`new-member-${field.key}`}>{field.label}</FieldLabel>
                              <Input
                                id={`new-member-${field.key}`}
                                type={field.type || 'text'}
                                value={newMemberData[field.key] ?? ''}
                                onChange={(e) => handleNewMemberFieldChange(field.key, e.target.value)}
                              />
                            </FieldGroup>
                          ))}
                        </EditGrid>
                      </FieldSection>
                    ))}

                    <EditActions>
                      <ActionButton $save onClick={saveNewMember}>
                        Save
                      </ActionButton>
                      <ActionButton $cancel onClick={closeAddMember}>
                        Cancel
                      </ActionButton>
                    </EditActions>
                  </ModalDialog>
                </ModalOverlay>
              )}

              {editingMemberId !== null && (
                <ModalOverlay onClick={cancelEditing}>
                  <ModalDialog onClick={(e) => e.stopPropagation()}>
                    <ModalHeader>
                      <ModalTitle>Edit Member Information</ModalTitle>
                      <ModalCloseButton type="button" onClick={cancelEditing}>
                        Close
                      </ModalCloseButton>
                    </ModalHeader>

                    <EditGrid>
                      {editableMemberFields.map((field) => (
                        <FieldGroup key={field.key}>
                          <FieldLabel htmlFor={`member-${field.key}`}>{field.label}</FieldLabel>
                          <Input
                            id={`member-${field.key}`}
                            type={field.type || 'text'}
                            value={editFormData[field.key] ?? ''}
                            onChange={(e) => handleMemberFieldChange(field.key, e.target.value)}
                          />
                        </FieldGroup>
                      ))}
                    </EditGrid>

                    <EditActions>
                      <ActionButton $save onClick={saveMemberUpdate}>
                        Save
                      </ActionButton>
                      <ActionButton $cancel onClick={cancelEditing}>
                        Cancel
                      </ActionButton>
                    </EditActions>
                  </ModalDialog>
                </ModalOverlay>
              )}
            </ContentSection>
          )}

          {/* Machineries Tab */}
          {activeTab === 'machineries' && (
            <ContentSection>
              <ContentHeader>
                <Title>Machineries Management</Title>
                <Subtitle>All machineries belonging to {iaUser.ia_name} for {activeYear || 'selected year'}</Subtitle>
              </ContentHeader>

              <MachineryToolbar>
                <ToolbarLeft>
                  <ActionButton $add onClick={openAddMachinery}>
                    Add Machinery
                  </ActionButton>
                  <ActionButton $add onClick={() => machineryFileInputRef.current?.click()} disabled={isImportingMachineries}>
                    {isImportingMachineries ? 'Importing...' : 'Import'}
                  </ActionButton>
                  <ActionButton $add onClick={handleExportMachineries} disabled={isExportingMachineries}>
                    {isExportingMachineries ? 'Exporting...' : 'Export'}
                  </ActionButton>
                  <input
                    ref={machineryFileInputRef}
                    type="file"
                    accept=".xlsx,.xls,.csv"
                    style={{ display: 'none' }}
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) handleImportMachineries(file);
                    }}
                  />
                </ToolbarLeft>
                <ToolbarRight>
                  <MembersSearchInput
                    type="text"
                    value={machinerySearchTerm}
                    onChange={(e) => setMachinerySearchTerm(e.target.value)}
                    placeholder="Search equipment, barangay, municipality, province"
                  />
                  <YearFilterGroup>
                    <YearFilterLabel htmlFor="machineries-year-filter"></YearFilterLabel>
                    <YearSelect
                      id="machineries-year-filter"
                      value={activeYear}
                      onChange={(e) => handleYearSelectionChange(e.target.value)}
                    >
                      {(yearOptions.length > 0 ? yearOptions : [iaYear]).filter(Boolean).map((year) => (
                        <option key={year} value={year}>{year}</option>
                      ))}
                    </YearSelect>
                  </YearFilterGroup>
                </ToolbarRight>
              </MachineryToolbar>

              {loading ? (
                <LoadingMessage>Loading machineries...</LoadingMessage>
              ) : machineries.length > 0 ? (
                <>
                  <TableWrapper>
                    <div id="scrollMachineries" style={{ width: '100%', overflowX: 'auto' }}>
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableHeader style={{ minWidth: 100 }}>Action</TableHeader>
                            {machineryTableColumns.map((column) => (
                              <TableHeader key={column.key} style={{ minWidth: getColMinWidth(column.key) }}>{column.label}</TableHeader>
                            ))}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {paginatedMachineries.items.map((machinery) => {
                            return (
                              <TableRow key={machinery.id}>
                                <ActionCell>
                                  <ActionButton $edit onClick={() => startEditingMachinery(machinery)}>
                                    Edit
                                  </ActionButton>
                                </ActionCell>
                                {machineryTableColumns.map((column) => (
                                  <TableCell key={`${machinery.id}-${column.key}`} style={{ minWidth: getColMinWidth(column.key) }}>
                                    {formatFieldValue(column.key, machinery[column.key])}
                                  </TableCell>
                                ))}
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </div>
                  </TableWrapper>

                  <PaginationContainer>
                    <PageButton
                      onClick={() => setMachineryPage((p) => Math.max(1, p - 1))}
                      disabled={paginatedMachineries.page <= 1}
                    >
                      Prev
                    </PageButton>
                    <PageInfo>
                      Page {paginatedMachineries.page} of {paginatedMachineries.totalPages} ({paginatedMachineries.total} items)
                    </PageInfo>
                    <PageButton
                      onClick={() => setMachineryPage((p) => Math.min(paginatedMachineries.totalPages, p + 1))}
                      disabled={paginatedMachineries.page >= paginatedMachineries.totalPages}
                    >
                      Next
                    </PageButton>
                  </PaginationContainer>
                </>
              ) : (
                <EmptyMessage>No machineries found.</EmptyMessage>
              )}

              {isAddMachineryOpen && (
                <ModalOverlay onClick={closeAddMachinery}>
                  <ModalDialog onClick={(e) => e.stopPropagation()}>
                    <ModalHeader>
                      <ModalTitle>Add Machinery</ModalTitle>
                      <ModalCloseButton type="button" onClick={closeAddMachinery}>
                        Close
                      </ModalCloseButton>
                    </ModalHeader>

                    <EditGrid>
                      {editableMachineryFields.map((field) => (
                        <FieldGroup key={field.key}>
                          <FieldLabel htmlFor={`new-machinery-${field.key}`}>{field.label}</FieldLabel>
                          <Input
                            id={`new-machinery-${field.key}`}
                            type={field.type || 'text'}
                            value={newMachineryData[field.key] ?? ''}
                            onChange={(e) => handleNewMachineryFieldChange(field.key, e.target.value)}
                          />
                        </FieldGroup>
                      ))}
                    </EditGrid>

                    <EditActions>
                      <ActionButton $save onClick={saveNewMachinery}>
                        Save
                      </ActionButton>
                      <ActionButton $cancel onClick={closeAddMachinery}>
                        Cancel
                      </ActionButton>
                    </EditActions>
                  </ModalDialog>
                </ModalOverlay>
              )}

              {editingMachineryId !== null && (
                <ModalOverlay onClick={cancelEditingMachinery}>
                  <ModalDialog onClick={(e) => e.stopPropagation()}>
                    <ModalHeader>
                      <ModalTitle>Edit Machinery Information</ModalTitle>
                      <ModalCloseButton type="button" onClick={cancelEditingMachinery}>
                        Close
                      </ModalCloseButton>
                    </ModalHeader>

                    <EditGrid>
                      {editableMachineryFields.map((field) => (
                        <FieldGroup key={field.key}>
                          <FieldLabel htmlFor={`machinery-${field.key}`}>{field.label}</FieldLabel>
                          <Input
                            id={`machinery-${field.key}`}
                            type={field.type || 'text'}
                            value={editMachineryData[field.key] ?? ''}
                            onChange={(e) => handleMachineryFieldChange(field.key, e.target.value)}
                          />
                        </FieldGroup>
                      ))}
                    </EditGrid>

                    <EditActions>
                      <ActionButton $save onClick={saveMachineryUpdate}>
                        Save
                      </ActionButton>
                      <ActionButton $cancel onClick={cancelEditingMachinery}>
                        Cancel
                      </ActionButton>
                    </EditActions>
                  </ModalDialog>
                </ModalOverlay>
              )}
            </ContentSection>
          )}

          {/* Profile Tab */}
          {activeTab === 'profile' && (
            <ContentSection>
              <ContentHeader style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                  <Title>IA Profile</Title>
                  <Subtitle>Profile details from the NIS table for {iaUser.ia_name}</Subtitle>
                </div>
                <div>
                  <ActionButton $edit onClick={startEditingProfile}>Edit Profile</ActionButton>
                </div>
              </ContentHeader>

              {loading ? (
                <LoadingMessage>Loading IA profile...</LoadingMessage>
              ) : profile ? (
                <div>
                  {profileFields.map((section, sectionIndex) => (
                    <ProfileSectionGroup key={sectionIndex}>
                      <SectionHeader>{section.category}</SectionHeader>
                      <ProfileGrid>
                        {section.fields.map((field) => (
                          <ProfileCard key={field.key}>
                            <ProfileLabel>{field.label}</ProfileLabel>
                            <ProfileValue>{(profile ? formatFieldValue(field.key, profile[field.key]) : '') || 'N/A'}</ProfileValue>
                          </ProfileCard>
                        ))}
                      </ProfileGrid>
                    </ProfileSectionGroup>
                  ))}
                </div>
              ) : (
                <EmptyMessage>No profile found for this IA.</EmptyMessage>
              )}
            </ContentSection>
          )}
        </MainContent>
      </Container>
    </Shell>
  );
};

export default DashboardIA;

const Shell = styled.div`
  min-height: 100vh;
  padding: 28px;
  overflow-x: hidden;
  background:
    radial-gradient(circle at top left, rgba(0, 255, 127, 0.2), transparent 30%),
    linear-gradient(135deg, #0a2311 0%, #0e4d23 40%, #f4f7f4 40%, #ffffff 100%);
`;

const Container = styled.div`
  display: flex;
  gap: 24px;
  min-height: calc(100vh - 56px);
  max-width: 1600px;
  margin: 0 auto;

  @media (max-width: 1024px) {
    flex-direction: column;
  }
`;

const Sidebar = styled.div`
  width: 280px;
  background: linear-gradient(135deg, #ffffff 0%, #f5fbf8 100%);
  border-radius: 32px;
  box-shadow: 0 20px 60px rgba(22, 64, 38, 0.15),
              0 0 1px rgba(0, 0, 0, 0.05);
  padding: 32px 24px;
  display: flex;
  flex-direction: column;
  height: fit-content;
  border: 1px solid rgba(46, 125, 50, 0.08);
  backdrop-filter: blur(10px);
  position: sticky;
  top: 28px;

  @media (max-width: 1024px) {
    width: 100%;
    height: auto;
    flex-direction: row;
    align-items: center;
    gap: 16px;
    position: static;
  }
`;

const SidebarHeader = styled.div`
  margin-bottom: 28px;
  padding-bottom: 24px;
  border-bottom: 2px solid rgba(46, 125, 50, 0.12);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;

  @media (max-width: 1024px) {
    flex: 0 0 auto;
    margin-bottom: 0;
    padding-bottom: 0;
    border-bottom: none;
    flex-direction: row;
    gap: 12px;
  }
`;

const LogoContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.3s ease;

  &:hover {
    transform: scale(1.05);
  }
`;

const LogoImage = styled.img`
  width: 100px;
  height: 100px;
  object-fit: contain;
  filter: drop-shadow(0 4px 12px rgba(46, 125, 50, 0.2));
  transition: all 0.3s ease;

  &:hover {
    filter: drop-shadow(0 6px 16px rgba(46, 125, 50, 0.35));
  }
`;

const Tag = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 10px 18px;
  border-radius: 12px;
  background: linear-gradient(135deg, #1f6b2a 0%, #2e7d32 100%);
  color: #ffffff;
  font-weight: 800;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  font-size: 11px;
  box-shadow: 0 4px 15px rgba(46, 125, 50, 0.25);
  border: 1px solid rgba(255, 255, 255, 0.2);
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(46, 125, 50, 0.35);
  }
`;

const NavTabs = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;

  @media (max-width: 1024px) {
    flex-direction: row;
    flex: 1;
  }
`;

const NavTab = styled.button`
  padding: 14px 18px;
  border: 2px solid transparent;
  border-radius: 14px;
  background: ${({ $active }) => ($active ? 'linear-gradient(135deg, #1f6b2a 0%, #2e7d32 100%)' : 'rgba(46, 125, 50, 0.05)')};
  color: ${({ $active }) => ($active ? '#ffffff' : '#2d5836')};
  font-weight: 700;
  font-size: 0.95rem;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;

  &:hover {
    background: ${({ $active }) => ($active ? 'linear-gradient(135deg, #2e7d32 0%, #388e3c 100%)' : 'rgba(46, 125, 50, 0.12)')};
    transform: translateX(4px);
    box-shadow: ${({ $active }) => ($active ? '0 8px 20px rgba(46, 125, 50, 0.3)' : '0 4px 12px rgba(46, 125, 50, 0.1)')};
  }

  &:active {
    transform: translateX(2px) scale(0.98);
  }

  @media (max-width: 1024px) {
    flex: 1;
    padding: 12px 14px;
    font-size: 0.9rem;
  }
`;

const LogoutSection = styled.div`
  margin-top: 28px;
  padding-top: 24px;
  border-top: 2px solid rgba(46, 125, 50, 0.12);

  @media (max-width: 1024px) {
    margin-top: 0;
    margin-left: auto;
    padding-top: 0;
    border-top: none;
  }
`;

const LogoutButton = styled.button`
  width: 100%;
  padding: 14px 18px;
  border: 2px solid #f44336;
  border-radius: 14px;
  background: rgba(244, 67, 54, 0.08);
  color: #d32f2f;
  font-weight: 800;
  font-size: 0.95rem;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
  letter-spacing: 0.02em;

  &:hover {
    background: linear-gradient(135deg, #ef5350 0%, #f44336 100%);
    color: #ffffff;
    border-color: #d32f2f;
    box-shadow: 0 8px 25px rgba(244, 67, 54, 0.35);
    transform: translateY(-2px);
  }

  &:active {
    transform: translateY(0) scale(0.97);
  }

  @media (max-width: 1024px) {
    width: auto;
    min-width: 140px;
    padding: 12px 16px;
    font-size: 0.9rem;
  }
`;

const MainContent = styled.div`
  flex: 1;
  background: rgba(255, 255, 255, 0.92);
  border-radius: 28px;
  box-shadow: 0 28px 80px rgba(0, 0, 0, 0.18);
  padding: 44px;
  min-height: calc(100vh - 56px);
  overflow-x: hidden;

  @media (max-width: 1024px) {
    min-height: auto;
  }
`;

const ContentSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const ContentHeader = styled.div`
  margin-bottom: 20px;
`;

const Title = styled.h1`
  margin: 0 0 10px;
  font-size: clamp(2rem, 4vw, 3.3rem);
  color: #10331b;
`;

const Subtitle = styled.p`
  margin: 0;
  color: #486052;
  font-size: 1.05rem;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 16px;

  @media (max-width: 1200px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const StatCard = styled.div`
  border: 1px solid #dce5dd;
  border-radius: 18px;
  padding: 18px;
  background: #fff;
`;

const StatLabel = styled.div`
  color: #6d8072;
  font-size: 0.88rem;
  margin-bottom: 6px;
  font-weight: 700;
`;

const StatValue = styled.div`
  color: #0f2716;
  font-size: 1.25rem;
  font-weight: 800;
`;

const TableWrapper = styled.div`
  overflow-x: auto;
  overflow-y: auto;
  max-height: calc(100vh - 300px);
  border-radius: 18px;
  border: 1px solid #b9d4c0;
  background: #e7f2ec;
  box-shadow: 0 12px 28px rgba(33, 70, 47, 0.12);
`;

const Table = styled.table`
  width: auto;
  display: inline-table;
  border-collapse: collapse;
  font-size: 0.9rem;
  min-width: max-content;
`;

const TableHead = styled.thead`
  background: linear-gradient(180deg, #2e7d32 0%, #1f6b2a 100%);
`;

const TableBody = styled.tbody``;

const TableRow = styled.tr`
  border-bottom: 1px solid #b9d4c0;
  background: #fff;

  &:nth-child(even) {
    background: #f7fbf8;
  }

  &:hover {
    background: #eef7f1;
  }

  &:last-child {
    border-bottom: none;
  }
`;

const TableHeader = styled.th`
  padding: 12px 14px;
  text-align: left;
  font-weight: 800;
  color: #fff;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  white-space: nowrap;
  border: 1px solid #0f4f1d;
  background: linear-gradient(180deg, #2e7d32 0%, #1f6b2a 100%);
  position: sticky;
  top: 0;
  z-index: 3;
  /* allow inline minWidth to control per-column sizing */
`;

const TableCell = styled.td`
  padding: 11px 14px;
  color: #203327;
  word-break: break-word;
  border: 1px solid #c7d9cd;
  white-space: nowrap;
  /* allow inline minWidth to control per-column sizing */
`;

const EditCell = styled(TableCell)`
  padding: 10px 8px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ActionCell = styled(TableCell)`
  display: flex;
  gap: 8px;
  justify-content: center;
  padding: 10px 8px;
  align-items: center;
`;

const EditPanel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 18px;
  background: #f7fbf8;
  border: 1px solid #c7d9cd;
  border-radius: 14px;
  padding: 16px;
`;

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.35);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 18px;
`;

const ModalDialog = styled.div`
  width: min(1100px, 96vw);
  max-height: 90vh;
  overflow: auto;
  background: #ffffff;
  border-radius: 14px;
  border: 1px solid #c7d9cd;
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.25);
  padding: 16px;
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 14px;
`;

const ModalTitle = styled.h3`
  margin: 0;
  color: #10331b;
`;

const ModalCloseButton = styled.button`
  padding: 8px 12px;
  border: 1px solid #c7d9cd;
  border-radius: 8px;
  background: #fff;
  color: #18421e;
  font-weight: 700;
  cursor: pointer;
`;

const EditGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(180px, 1fr));
  gap: 12px;

  @media (max-width: 1100px) {
    grid-template-columns: repeat(2, minmax(180px, 1fr));
  }

  @media (max-width: 700px) {
    grid-template-columns: 1fr;
  }
`;

const FieldSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 18px;
`;

const SectionTitle = styled.div`
  font-size: 0.9rem;
  font-weight: 800;
  color: #18591d;
  text-transform: uppercase;
  letter-spacing: 0.06em;
`;

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const FieldLabel = styled.label`
  font-size: 0.82rem;
  font-weight: 700;
  color: #486052;
`;

const EditActions = styled.div`
  display: flex;
  gap: 10px;
  justify-content: flex-end;
`;

const Input = styled.input`
  padding: 8px 10px;
  border: 1px solid #a9c4af;
  border-radius: 6px;
  font-size: 0.9rem;
  font-family: inherit;
  background: #fff;

  &:focus {
    outline: none;
    border-color: #1f6b2a;
    box-shadow: 0 0 0 3px rgba(46, 125, 50, 0.12);
  }
`;

const ActionButton = styled.button`
  padding: 8px 14px;
  border: none;
  border-radius: 8px;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  white-space: nowrap;

  ${({ $edit }) =>
    $edit &&
    `
    background: #2196f3;
    color: white;
    
    &:hover {
      background: #1976d2;
      transform: scale(1.05);
    }
  `}

  ${({ $save }) =>
    $save &&
    `
    background: #4caf50;
    color: white;
    
    &:hover {
      background: #45a049;
      transform: scale(1.05);
    }
  `}

  ${({ $add }) =>
    $add &&
    `
    background: #18591d;
    color: white;
    
    &:hover {
      background: #124517;
      transform: scale(1.05);
    }
  `}

  ${({ $cancel }) =>
    $cancel &&
    `
    background: #f44336;
    color: white;
    
    &:hover {
      background: #d32f2f;
      transform: scale(1.05);
    }
  `}
`;

const LoadingMessage = styled.div`
  text-align: center;
  padding: 40px;
  color: #666;
  font-size: 1.1rem;
`;

const EmptyMessage = styled.div`
  text-align: center;
  padding: 40px;
  color: #999;
  font-size: 1.1rem;
`;

const PaginationContainer = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
  justify-content: flex-end;
  margin-top: 12px;
`;

const PageButton = styled.button`
  padding: 6px 10px;
  border-radius: 8px;
  border: 1px solid #c7d9cd;
  background: #fff;
  cursor: pointer;
  font-weight: 700;
  color: #18421e;
  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
`;

const PageInfo = styled.div`
  color: #486052;
  font-size: 0.95rem;
  font-weight: 700;
`;

const AnalyticsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const ChartCard = styled.div`
  border: 1px solid #dce5dd;
  border-radius: 18px;
  padding: 18px;
  background: linear-gradient(180deg, #ffffff 0%, #f8fcf9 100%);
  box-shadow: 0 10px 24px rgba(22, 64, 38, 0.08);
`;

const ChartTitle = styled.h3`
  margin: 0 0 14px;
  color: #10331b;
  font-size: 1rem;
`;

const MetricRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 12px;
`;

const MetricLabel = styled.div`
  color: #486052;
  font-size: 0.88rem;
  font-weight: 700;
`;

const MetricTrack = styled.div`
  width: 100%;
  height: 10px;
  border-radius: 999px;
  background: #e6efe9;
  overflow: hidden;
`;

const MetricFill = styled.div`
  height: 100%;
  border-radius: inherit;
  transition: width 0.4s ease;
`;

const DonutWrap = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 12px;
`;

const DonutRing = styled.div`
  width: 180px;
  height: 180px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const DonutCenter = styled.div`
  width: 118px;
  height: 118px;
  border-radius: 50%;
  background: #fff;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  box-shadow: inset 0 0 0 1px #dce5dd;
`;

const DonutNumber = styled.div`
  color: #10331b;
  font-size: 1.2rem;
  font-weight: 800;
`;

const DonutText = styled.div`
  color: #6d8072;
  font-size: 0.78rem;
  font-weight: 700;
  text-transform: uppercase;
`;

const LegendRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 6px;
`;

const LegendSwatch = styled.div`
  width: 10px;
  height: 10px;
  border-radius: 50%;
`;

const LegendText = styled.div`
  color: #486052;
  font-size: 0.86rem;
  font-weight: 700;
`;

const ProfileMiniCard = styled.div`
  border: 1px solid #dce5dd;
  border-radius: 14px;
  padding: 14px 16px;
  background: #fff;
`;

const ProfileMiniTitle = styled.div`
  color: #10331b;
  font-size: 0.9rem;
  font-weight: 800;
  margin-bottom: 6px;
`;

const ProfileMiniText = styled.div`
  color: #486052;
  font-size: 0.9rem;
  font-weight: 600;
`;

const ProfileGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 14px;

  @media (max-width: 1200px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ProfileSectionGroup = styled.div`
  margin-bottom: 32px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const SectionHeader = styled.h3`
  margin: 0 0 16px 0;
  padding-bottom: 12px;
  color: #1f6b2a;
  font-size: 1.05rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  border-bottom: 3px solid rgba(46, 125, 50, 0.2);
`;

const ProfileCard = styled.div`
  border: 1px solid #dce5dd;
  border-radius: 14px;
  padding: 14px;
  background: #fff;
`;

const ProfileLabel = styled.div`
  color: #6d8072;
  font-size: 0.82rem;
  margin-bottom: 6px;
  font-weight: 700;
  text-transform: uppercase;
`;

const ProfileValue = styled.div`
  color: #0f2716;
  font-size: 0.98rem;
  font-weight: 700;
`;

const MembersToolbar = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
`;

const MachineryToolbar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  margin-bottom: 4px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const ToolbarLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
`;

const ToolbarRight = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  flex: 1;
  gap: 12px;
  flex-wrap: wrap;
`;

const YearFilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 170px;
  flex: 0 0 auto;
`;

const YearFilterLabel = styled.label`
  font-size: 0.82rem;
  font-weight: 700;
  color: #486052;
`;

const YearSelect = styled.select`
  padding: 10px 14px;
  border: 1px solid #a9c4af;
  border-radius: 10px;
  font-size: 0.95rem;
  font-family: inherit;
  background: #fff;
  color: #203327;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: #1f6b2a;
    box-shadow: 0 0 0 3px rgba(46, 125, 50, 0.12);
  }
`;

const MembersSearchInput = styled.input`
  width: min(420px, 100%);
  padding: 10px 14px;
  border: 1px solid #a9c4af;
  border-radius: 10px;
  font-size: 0.95rem;
  font-family: inherit;
  background: #fff;

  &:focus {
    outline: none;
    border-color: #1f6b2a;
    box-shadow: 0 0 0 3px rgba(46, 125, 50, 0.12);
  }
`;